import * as React from 'react';
import clsx from 'clsx';

// Main Hint component interface
export interface HintProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Optional icon to display */
  icon?: React.ReactNode;
  /** Icon size - component controls this automatically */
  iconSize?: number;
  /** Error state styling */
  hasError?: boolean;
  /** Disabled state styling */
  disabled?: boolean;
  /** Custom className */
  className?: string;
  /** Hint text content */
  children?: React.ReactNode;
}

/**
 * Hint - Simple hint component for displaying helper text with optional icon
 * Supports error and disabled states
 * The component automatically controls icon size for consistency
 */
export const Hint = React.forwardRef<HTMLDivElement, HintProps>(
  (
    {
      children,
      icon,
      iconSize = 16,
      hasError = false,
      disabled = false,
      className,
      ...props
    },
    ref
  ) => {
    // Clone icon with controlled size if it's a React element
    const clonedIcon = React.useMemo(() => {
      if (!icon) return null;

      // If icon is a React element, clone it with size prop
      if (React.isValidElement(icon)) {
        const iconElement = icon as React.ReactElement<{ size?: number }>;
        return React.cloneElement(iconElement, {
          // Preserve any existing props but override size
          ...iconElement.props,
          size: iconSize,
        });
      }

      // If it's not a React element, return as is
      return icon;
    }, [icon, iconSize]);

    return (
      <div
        ref={ref}
        className={clsx(
          'hint',
          {
            'hint--disabled': disabled,
            'hint--error': hasError,
          },
          className
        )}
        {...props}
      >
        {clonedIcon && (
          <div
            className={clsx('hint__icon', {
              'hint__icon--disabled': disabled,
              'hint__icon--error': hasError,
            })}
          >
            {clonedIcon}
          </div>
        )}
        {children && <div className="hint__text">{children}</div>}
      </div>
    );
  }
);

Hint.displayName = 'Hint';

// Legacy compound component exports for backward compatibility
export interface HintRootProps extends React.HTMLAttributes<HTMLDivElement> {
  hasError?: boolean;
  disabled?: boolean;
  className?: string;
  children?: React.ReactNode;
}

export interface HintIconProps extends React.HTMLAttributes<HTMLDivElement> {
  hasError?: boolean;
  disabled?: boolean;
  className?: string;
  children?: React.ReactNode;
  as?: React.ElementType;
}

// Shared props interface for context
interface HintContextProps {
  hasError?: boolean;
  disabled?: boolean;
}

// Context for sharing state between Root and Icon
const HintContext = React.createContext<HintContextProps>({});

/**
 * HintRoot - Legacy compound component (deprecated, use Hint instead)
 */
export const HintRoot = React.forwardRef<HTMLDivElement, HintRootProps>(
  (
    { children, hasError = false, disabled = false, className, ...props },
    ref
  ) => {
    const contextValue = React.useMemo(
      () => ({ hasError, disabled }),
      [hasError, disabled]
    );

    return (
      <HintContext.Provider value={contextValue}>
        <div
          ref={ref}
          className={clsx(
            'hint',
            {
              'hint--disabled': disabled,
              'hint--error': hasError,
            },
            className
          )}
          {...props}
        >
          {children}
        </div>
      </HintContext.Provider>
    );
  }
);

HintRoot.displayName = 'HintRoot';

/**
 * HintIcon - Legacy compound component (deprecated, use Hint instead)
 */
export const HintIcon = React.forwardRef<HTMLDivElement, HintIconProps>(
  (
    {
      className,
      children,
      as: Component = 'div',
      hasError: propHasError,
      disabled: propDisabled,
      ...props
    },
    ref
  ) => {
    const context = React.useContext(HintContext);

    // Props override context
    const hasError = propHasError ?? context.hasError ?? false;
    const disabled = propDisabled ?? context.disabled ?? false;

    return (
      <Component
        ref={ref}
        className={clsx(
          'hint__icon',
          {
            'hint__icon--disabled': disabled,
            'hint__icon--error': hasError,
          },
          className
        )}
        {...props}
      >
        {children}
      </Component>
    );
  }
);

HintIcon.displayName = 'HintIcon';

// Individual exports for convenience
export { HintRoot as Root, HintIcon as Icon };
